import React, { useEffect, useRef, useState } from 'react';
import * as THREE from 'three';
import { STLLoader } from 'three/examples/jsm/loaders/STLLoader';
import { PLYLoader } from 'three/examples/jsm/loaders/PLYLoader';
import { OrbitControls } from 'three/examples/jsm/controls/OrbitControls';
import { mergeVertices } from 'three/examples/jsm/utils/BufferGeometryUtils';

const ThreeDViewer = ({ url, extension }) => {
  const mountRef = useRef(null);
  const sceneRef = useRef({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!url || !mountRef.current) return;

    const mount = mountRef.current;
    setLoading(true);
    setError(null);

    // Scene
    const scene = new THREE.Scene();
    scene.background = new THREE.Color(0x1e1e2e);

    // Camera
    const camera = new THREE.PerspectiveCamera(45, mount.clientWidth / mount.clientHeight, 0.001, 10000);

    // Renderer — cap pixel ratio to avoid excessive GPU load on HiDPI displays
    const renderer = new THREE.WebGLRenderer({ antialias: true, powerPreference: 'high-performance' });
    renderer.setSize(mount.clientWidth, mount.clientHeight);
    renderer.setPixelRatio(Math.min(window.devicePixelRatio, 1.5));
    mount.appendChild(renderer.domElement);

    // Lighting
    scene.add(new THREE.AmbientLight(0xffffff, 0.5));
    const dirLight1 = new THREE.DirectionalLight(0xffffff, 0.8);
    dirLight1.position.set(1, 2, 3);
    scene.add(dirLight1);
    const dirLight2 = new THREE.DirectionalLight(0xffffff, 0.3);
    dirLight2.position.set(-2, -1, -2);
    scene.add(dirLight2);

    // Controls
    const controls = new OrbitControls(camera, renderer.domElement);
    controls.enableDamping = true;
    controls.dampingFactor = 0.05;

    // On-demand rendering — only re-render when something changes.
    // dirty=false BEFORE controls.update() so that if update() fires 'change'
    // (damping still decelerating), the flag is set again for the next frame.
    let dirty = false;
    const markDirty = () => { dirty = true; };
    controls.addEventListener('change', markDirty);
    controls.addEventListener('start', markDirty);

    // Load geometry
    const loader = extension === 'ply' ? new PLYLoader() : new STLLoader();

    loader.load(
      url,
      (geometry) => {
        // Index the geometry if not already indexed (common for PLY).
        // Reduces GPU vertex buffer size and improves vertex cache efficiency.
        let geo = geometry;
        if (!geo.index) {
          try { geo = mergeVertices(geo); } catch (_) {}
        }
        geo.computeVertexNormals();

        // Lambert: per-vertex shading, much cheaper than Phong on dense meshes.
        const material = new THREE.MeshLambertMaterial({ color: 0x4a8fe7 });
        const mesh = new THREE.Mesh(geo, material);
        scene.add(mesh);

        // Center and fit camera
        const box = new THREE.Box3().setFromObject(mesh);
        const center = box.getCenter(new THREE.Vector3());
        const size = box.getSize(new THREE.Vector3());
        const maxDim = Math.max(size.x, size.y, size.z);

        mesh.position.sub(center);
        camera.position.set(0, maxDim * 0.5, maxDim * 2);
        camera.near = maxDim * 0.001;
        camera.far = maxDim * 100;
        camera.updateProjectionMatrix();
        controls.target.set(0, 0, 0);
        controls.update();

        markDirty();
        setLoading(false);
      },
      undefined,
      () => {
        setError('Impossible de charger le fichier 3D.');
        setLoading(false);
      }
    );

    // Resize
    const handleResize = () => {
      if (!mount) return;
      camera.aspect = mount.clientWidth / mount.clientHeight;
      camera.updateProjectionMatrix();
      renderer.setSize(mount.clientWidth, mount.clientHeight);
      markDirty();
    };
    window.addEventListener('resize', handleResize);

    // Animation loop — skips render when nothing has changed
    let animId;
    const animate = () => {
      animId = requestAnimationFrame(animate);
      if (!dirty) return;
      dirty = false; // clear before update so damping 'change' events re-set it
      controls.update();
      renderer.render(scene, camera);
    };
    animate();

    sceneRef.current = { renderer, controls, animId };

    return () => {
      cancelAnimationFrame(animId);
      window.removeEventListener('resize', handleResize);
      controls.removeEventListener('change', markDirty);
      controls.removeEventListener('start', markDirty);
      controls.dispose();
      renderer.dispose();
      if (mount.contains(renderer.domElement)) {
        mount.removeChild(renderer.domElement);
      }
    };
  }, [url, extension]);

  return (
    <div className="threed-viewer-wrapper">
      {loading && !error && (
        <div className="viewer-overlay">
          <div className="spinner"></div>
          <p>Chargement du modèle...</p>
        </div>
      )}
      {error && (
        <div className="viewer-overlay viewer-error">
          <i className="material-icons">broken_image</i>
          <p>{error}</p>
        </div>
      )}
      <div ref={mountRef} className="threed-viewer-canvas" />
      <div className="viewer-controls-hint">
        <span><i className="material-icons">mouse</i> Clic gauche : rotation</span>
        <span><i className="material-icons">open_with</i> Clic droit : déplacement</span>
        <span><i className="material-icons">zoom_in</i> Molette : zoom</span>
      </div>
    </div>
  );
};

export default ThreeDViewer;
